/**
 * Analiz katman iskeletleri â€” owner bazlÄ± kayÄ±t.
 */
import {
  EMPTY_FC,
  GTFS_MAP_LAYERS,
  GTFS_MAP_SOURCES,
  ADMIN_CATEGORY_FILL,
  LAYER_CATEGORIES,
  LAYER_OWNERS,
  SA_BUFFER_LAYER_BY_CATEGORY,
} from './gtfsMapLayers.js'
import {
  isochroneSequentialFillExpr,
  isochroneFillOpacityExpr,
  saSequentialFillExpr,
  saCategoryMatchFillExpr,
  saCoverageChoroplethFillExpr,
  transitDesertGapFillExpr,
  transitDesertSupplyFillExpr,
  coverageChoroplethFillExpr,
  accessibilityCoverageFillExpr,
} from './analysisSemanticColors.js'
import { useMapLayerStore } from '@/stores/mapLayer.store'
import {
  isOwnerLayersReady,
  markOwnerLayersReady,
  resetOwnerLayerCache,
} from './ownerLayerCache.js'
import { raiseReferenceLayers } from './bootstrapSharedMapLayers.js'
import { MAP_FONT_MEDIUM, MAP_FONT_REGULAR } from './gtfsBasemap.js'

export { resetOwnerLayerCache }

const SA_BUFFER_ORDER = [
  'no_service',
  'sparse',
  'limited',
  'regular',
  'frequent',
]

/** Analiz katmanlarÄ± eklenince sÄ±ra yeniden kurulur (dolgular altta, aÄŸ ve etiketler Ã¼stte) */
function raiseSharedReferenceLayers() {
  raiseReferenceLayers()
}

export function ensureOwnerAnalysisLayers(ownerId) {
  if (!ownerId || ownerId === 'shared') return
  const store = useMapLayerStore()
  if (isOwnerLayersReady(ownerId)) {
    const still = store.byOwner(ownerId).some((l) => l.category === 'analysis')
    if (still) {
      raiseSharedReferenceLayers()
      return
    }
  }
  if (ownerId === LAYER_OWNERS.isochrone) registerIsochrone(store)
  else if (ownerId === LAYER_OWNERS.catchment) registerCatchment(store)
  else if (ownerId === LAYER_OWNERS.corridor) registerCorridor(store)
  else if (ownerId === LAYER_OWNERS.serviceAvailability)
    registerServiceAvailability(store)
  else if (ownerId === LAYER_OWNERS.transitDesert) registerTransitDesert(store)
  markOwnerLayersReady(ownerId)
  raiseSharedReferenceLayers()
}

export function registerAnalysisLayerCatalog() {
  Object.values(LAYER_OWNERS).forEach((id) => {
    if (id !== 'shared') ensureOwnerAnalysisLayers(id)
  })
}

function registerIsochrone(store) {
  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.isochrone,
    'detail',
    {
      sourceId: GTFS_MAP_SOURCES.isochrones,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.isochroneFill,
          type: 'fill',
          paint: {
            'fill-color': isochroneSequentialFillExpr(),
            // _dim: çok modlu sonuçta odak dışı mod — yalnızca sınır çizgisi
            'fill-opacity': [
              'case',
              ['boolean', ['get', '_dim'], false],
              0,
              isochroneFillOpacityExpr(),
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.isochroneLine,
          type: 'line',
          paint: {
            'line-color': isochroneSequentialFillExpr(),
            'line-width': ['case', ['boolean', ['get', '_dim'], false], 1.2, 1.6],
            'line-opacity': ['case', ['boolean', ['get', '_dim'], false], 0.7, 0.85],
          },
        },
      ],
    },
    { label: 'Alan', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.isochrone,
    'coverage',
    {
      sourceId: GTFS_MAP_SOURCES.isochroneCoverage,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.isochroneCoverageFill,
          type: 'fill',
          paint: { 'fill-color': '#7e22ce', 'fill-opacity': 0.28 },
        },
        {
          id: GTFS_MAP_LAYERS.isochroneCoverageLine,
          type: 'line',
          paint: {
            'line-color': '#c084fc',
            'line-width': 2,
            'line-opacity': 0.9,
          },
        },
      ],
    },
    { label: 'Kapsama', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.isochrone,
    'administrative',
    {
      sourceId: GTFS_MAP_SOURCES.isochroneAdmin,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.isochroneAdminFill,
          type: 'fill',
          paint: {
            'fill-color': accessibilityCoverageFillExpr('covered_pct'),
            'fill-opacity': 0.55,
          },
        },
        {
          id: GTFS_MAP_LAYERS.isochroneAdminLine,
          type: 'line',
          paint: {
            'line-color': 'rgba(192, 132, 252, 0.7)',
            'line-width': 0.8,
          },
        },
      ],
    },
    { label: 'Mahalle', visible: false }
  )
}

function registerCatchment(store) {
  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.catchment,
    'detail',
    {
      sourceId: GTFS_MAP_SOURCES.catchmentIso,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.catchmentFill,
          type: 'fill',
          paint: { 'fill-color': '#9333ea', 'fill-opacity': 0.18 },
        },
      ],
    },
    { label: 'Alan', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.catchment,
    'coverage',
    {
      sourceId: GTFS_MAP_SOURCES.catchmentCoverage,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.catchmentCoverageFill,
          type: 'fill',
          paint: { 'fill-color': '#7e22ce', 'fill-opacity': 0.28 },
        },
        {
          id: GTFS_MAP_LAYERS.catchmentCoverageLine,
          type: 'line',
          paint: { 'line-color': '#c084fc', 'line-width': 1.8 },
        },
      ],
    },
    { label: 'Kapsama', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.catchment,
    'administrative',
    {
      sourceId: GTFS_MAP_SOURCES.catchmentAdmin,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.catchmentAdminFill,
          type: 'fill',
          paint: {
            'fill-color': accessibilityCoverageFillExpr('covered_pct'),
            'fill-opacity': 0.55,
          },
        },
        {
          id: GTFS_MAP_LAYERS.catchmentAdminLine,
          type: 'line',
          paint: {
            'line-color': 'rgba(192, 132, 252, 0.65)',
            'line-width': 0.8,
          },
        },
      ],
    },
    { label: 'Mahalle', visible: false }
  )
}

function registerCorridor(store) {
  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.corridor,
    'detail',
    {
      sourceId: GTFS_MAP_SOURCES.corridorLine,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.corridorLine,
          type: 'line',
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'selected'], true],
              '#b91c1c',
              ['>', ['coalesce', ['get', 'severity'], 0], 8],
              '#c2410c',
              '#ea580c',
            ],
            'line-width': ['case', ['==', ['get', 'selected'], true], 7, 4],
            'line-opacity': [
              'case',
              ['==', ['get', 'selected'], true],
              0.95,
              ['==', ['get', 'dimmed'], true],
              0.25,
              0.75,
            ],
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        },
        {
          id: GTFS_MAP_LAYERS.corridorHit,
          type: 'line',
          paint: {
            'line-color': '#000000',
            'line-width': 18,
            'line-opacity': 0,
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        },
      ],
    },
    { label: 'Katarlanma Koridoru', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.interaction,
    LAYER_OWNERS.corridor,
    'area',
    {
      sourceId: GTFS_MAP_SOURCES.corridorArea,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.corridorAreaFill,
          type: 'fill',
          paint: {
            'fill-color': 'rgba(239, 68, 68, 0.12)',
            'fill-opacity': 1,
          },
        },
        {
          id: GTFS_MAP_LAYERS.corridorAreaLine,
          type: 'line',
          paint: {
            'line-color': '#f87171',
            'line-width': 1.5,
            'line-dasharray': [2, 1],
          },
        },
      ],
    },
    { label: 'Alan Secimi', visible: false }
  )
}

function registerServiceAvailability(store) {
  const saBufferLayers = SA_BUFFER_ORDER.map((cat) => ({
    id: SA_BUFFER_LAYER_BY_CATEGORY[cat],
    type: 'fill',
    filter: ['==', ['get', 'category'], cat],
    paint: {
      'fill-color': saCategoryMatchFillExpr('category'),
      'fill-opacity': 0.48,
    },
  }))

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.serviceAvailability,
    'detail-buffers',
    {
      sourceId: GTFS_MAP_SOURCES.saBuffers,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: saBufferLayers,
    },
    { label: 'Buffer', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.serviceAvailability,
    'detail-stops',
    {
      sourceId: GTFS_MAP_SOURCES.saStops,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.saStopsHit,
          type: 'circle',
          paint: {
            'circle-radius': 14,
            'circle-color': '#000000',
            'circle-opacity': 0,
          },
        },
        {
          id: GTFS_MAP_LAYERS.saStopsCircle,
          type: 'circle',
          paint: {
            'circle-radius': 6,
            'circle-color': saSequentialFillExpr(),
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.95,
          },
        },
      ],
    },
    { label: 'Duraklar', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.serviceAvailability,
    'coverage',
    {
      sourceId: GTFS_MAP_SOURCES.saCoverage,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.saCoverageFill,
          type: 'fill',
          paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.3 },
        },
        {
          id: GTFS_MAP_LAYERS.saCoverageLine,
          type: 'line',
          paint: { 'line-color': '#60a5fa', 'line-width': 1.8 },
        },
      ],
    },
    { label: 'Kapsama', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.serviceAvailability,
    'administrative',
    {
      sourceId: GTFS_MAP_SOURCES.adminMahalle,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.adminMahalleFill,
          type: 'fill',
          paint: {
            'fill-color': saCoverageChoroplethFillExpr('covered_pct', 'best_category'),
            'fill-opacity': [
              'case',
              ['==', ['get', 'selected'], true],
              0.72,
              0.42,
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.adminMahalleLine,
          type: 'line',
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'selected'], true],
              '#e2e8f0',
              'rgba(148, 163, 184, 0.55)',
            ],
            'line-width': [
              'case',
              ['==', ['get', 'selected'], true],
              2.2,
              0.6,
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.adminMahalleLabel,
          type: 'symbol',
          minzoom: 12.5,
          layout: {
            'text-field': ['coalesce', ['get', 'label'], ['get', 'mahalle'], ''],
            'text-font': MAP_FONT_REGULAR,
            'text-size': 11,
            'text-optional': true,
          },
          paint: {
            'text-color': '#e2e8f0',
            'text-halo-color': '#0f172a',
            'text-halo-width': 1.2,
          },
        },
      ],
    },
    { label: 'Mahalle', visible: false }
  )

  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.serviceAvailability,
    'ilce-choropleth',
    {
      sourceId: GTFS_MAP_SOURCES.adminIlce,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.adminIlceFill,
          type: 'fill',
          paint: {
            'fill-color': saCoverageChoroplethFillExpr('covered_pct', 'best_category'),
            'fill-opacity': [
              'case',
              ['==', ['get', 'selected'], true],
              0.68,
              0.4,
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.adminIlceLine,
          type: 'line',
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'selected'], true],
              '#e2e8f0',
              'rgba(148, 163, 184, 0.65)',
            ],
            'line-width': [
              'case',
              ['==', ['get', 'selected'], true],
              2.4,
              1.2,
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.adminIlceLabel,
          type: 'symbol',
          minzoom: 9,
          layout: {
            'text-field': ['coalesce', ['get', 'label'], ['get', 'ilce'], ''],
            'text-font': MAP_FONT_MEDIUM,
            'text-size': 12,
            'text-optional': true,
          },
          paint: {
            'text-color': '#e2e8f0',
            'text-halo-color': '#0f172a',
            'text-halo-width': 1.4,
          },
        },
      ],
    },
    { label: 'Ä°lÃ§e', visible: false }
  )
  void ADMIN_CATEGORY_FILL
}

function registerTransitDesert(store) {
  store.registerLayer(
    LAYER_CATEGORIES.analysis,
    LAYER_OWNERS.transitDesert,
    'administrative',
    {
      sourceId: GTFS_MAP_SOURCES.transitDesert,
      source: { type: 'geojson', data: EMPTY_FC },
      layers: [
        {
          id: GTFS_MAP_LAYERS.transitDesertFill,
          type: 'fill',
          paint: {
            'fill-color': transitDesertGapFillExpr(),
            'fill-opacity': [
              'case',
              ['==', ['get', 'selected'], true],
              0.75,
              0.5,
            ],
          },
        },
        {
          id: GTFS_MAP_LAYERS.transitDesertLine,
          type: 'line',
          paint: {
            'line-color': [
              'case',
              ['==', ['get', 'selected'], true],
              '#f8fafc',
              'rgba(148, 163, 184, 0.5)',
            ],
            'line-width': [
              'case',
              ['==', ['get', 'selected'], true],
              2.4,
              0.7,
            ],
          },
        },
      ],
    },
    { label: 'Transit Desert', visible: false }
  )
}
